import clsx from 'clsx'
import React, { useState } from 'react'

import { ampli } from '@Analytics/ampli'
import AddToCart2SVG from '@Assets/SVG/AddToCart2'
import CheckRoundedSVG from '@Assets/SVG/CheckRounded'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { ProductTileWrapper } from '@Components/ProductList/ProductTileWrapper'
import { Skeleton, SkeletonList } from '@Components/Skeleton/Skeleton'
import { Button } from '@CoreComponents/Buttons/Button'
import { Group, useProductTacticWithRecommendations } from '@Domains/Product/Features/Tactics/Hook'
import { ProductTile } from '@Domains/Product/Tiles/Tile'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './grid.module.scss'

export const RecommendationGrid = ({
  group,
  id,
  productID,
}: {
  group: Group
  id: string
  productID: string
}) => {
  const { storeURL } = React.useContext(MetadataContext)
  const [ref, loading, recommendation, products] = useProductTacticWithRecommendations(productID, group, id)
  const [addedToCart, setAddedToCart] = useState<string[]>([])
  const [showMore, setShowMore] = useState(true)
  const { t } = useTranslation()

  const showMoreButton = (
    <Button
      className={clsx(styles.showMore, (!showMore || products.length <= 18) && styles.hidden)}
      disabled={loading}
      innerSpacing="large"
      onClick={() => {
        setShowMore(false)
        ampli.plApplyPage({ page: 2, source_id: 'recommendation_grid_show_more' })
      }}
      type="button"
    >
      {t('cms:SHOW_MORE_LABEL')}
    </Button>
  )

  if (loading) {
    return (
      <div className={styles.grid} ref={ref}>
        <h3 className={styles.gridHeader}>
          <Skeleton className={styles.skeleton} />
        </h3>

        <ProductTileWrapper maxColumns={8} showMore={showMore}>
          <SkeletonList count={25} />
        </ProductTileWrapper>

        {showMoreButton}
      </div>
    )
  }

  if (!recommendation || products.length === 0) {
    return <div ref={ref} />
  }

  const sourceID = `product_page_${recommendation.placement}_${recommendation.id}`

  return (
    <div className={styles.grid} data-in-view={sourceID} ref={ref}>
      <h3 className={styles.gridHeader}>{recommendation.title}</h3>

      <ProductTileWrapper maxColumns={8} showMore={showMore}>
        {products.map(product => (
          <ProductTile
            key={`recommendation_${product.id}`}
            {...product}
            action={(
              <BuyButtonV2
                className={clsx(addedToCart.includes(product.id) && styles.success)}
                color="black"
                onSuccess={() => {
                  setAddedToCart((prev) => {
                    if (prev.includes(product.id)) {
                      return prev
                    }

                    return [...prev, product!.id]
                  })
                }}
                portal
                preventDefault
                productID={product.id}
                products={[{ quantity: 1, productID: product.id }]}
                sourceID={sourceID}
                stopPropagation
                useCache
              >
                {addedToCart.includes(product.id) ? <CheckRoundedSVG /> : <AddToCart2SVG />}
              </BuyButtonV2>
            )}
            sourceID={sourceID}
            url={storeURL(product.url || '')}
          />
        ))}
      </ProductTileWrapper>

      {showMoreButton}
    </div>
  )
}
